import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import styled, { css, ThemeProvider } from 'styled-components';
import { useQuery } from '@apollo/react-hooks';
import Layout from '../common/Layout/Layout';
import { Column, GridContainer, Row } from '../common/GridLayout/GridLayout';
import Spinner from '../common/Spinner/Spinner';
import classAssignmentSubmissionPage from '../../apollo/queries/classAssignmentSubmissionPage.graphql';
import ChevronLeft from '../common/Icons/ChevronLeft';
import ChevronRight from '../common/Icons/ChevronRight';
import {
  ATTACHMENT_CONTEXT_ACTIONS,
  useAssignmentAttachmentDispatch,
} from '../AssignmentSubmission/AssignmentAttachmentContext';
import { getLangCode } from '../../utils/language';
import { useErrorHandler } from '../common/ErrorHandler/ErrorHandler';
import AssignmentContext from '../common/AssignmentContext/AssignmentContext';
import assignmentTypes from '../../utils/assignmentTypes';
import { SubmissionInfo } from './SubmissionInfo';
import { SubmissionDetail } from './SubmissionDetails';
import {
  useStudentSubmissionDispatch,
  useStudentSubmissionState,
} from './StudentSubmissionPageContext';
import UnsavedChangesSubmissionConfirmationModal from './UnsavedChangesSubmissionConfirmationModal';

const arrowContainerCss = css`
  width: 32px;
  height: 32px;
  box-shadow: 0 2px 5px 0 rgba(0, 0, 0, 0.1);
  background-color: #ffffff;
  position: absolute;
  top: 30%;
  transform: translateY(-50%);
  border-radius: 32px;
  cursor: pointer;
  transition: 0.2s box-shadow;

  :hover {
    box-shadow: 0 4px 10px 0 rgba(0, 0, 0, 0.2);
  }

  & > svg {
    position: absolute;
    color: #485465;
    top: calc(50% + 1px);
    transform: translate(-50%, -50%);
  }
`;

const PreviousStudentButton = styled.div`
  ${arrowContainerCss};
  left: 13px;

  & > svg {
    left: calc(50% + 1px);
  }
`;
const NextStudentButton = styled.div`
  ${arrowContainerCss};
  right: 13px;

  & > svg {
    left: calc(50% - 1px);
  }
`;

const Container = styled(GridContainer)`
  ${({ theme }) => (theme.dir === 'rtl' ? 'direction: rtl' : '')};
`;

const StudentSubmission = ({
  teacherAssignmentParams,
  alefAssignmentParams,
  studentId,
  assignmentType,
  isGradableItemsLockedByDefault,
  onGoBack,
  onMoveToNextStudent,
  onMoveToPrevStudent,
  onSubmissionsReturn,
  finishExperienceContext,
}) => {
  const { t, dir } = useTranslation();
  const dispatchAttachment = useAssignmentAttachmentDispatch();
  const dispatch = useStudentSubmissionDispatch();
  const state = useStudentSubmissionState();
  const { handleError } = useErrorHandler();
  const handleErrorCallback = useCallback(handleError, []);

  const { assignmentInstanceId } = teacherAssignmentParams;
  const { assignmentId } = alefAssignmentParams;
  const { mloId, contentId, classId, academicYearId } =
    assignmentType === assignmentTypes.TEACHER
      ? teacherAssignmentParams
      : alefAssignmentParams;

  const { data = {}, loading } = useQuery(classAssignmentSubmissionPage, {
    variables: {
      input: {
        type: assignmentType,
        assignmentId,
        referrerId: assignmentInstanceId || mloId,
        lessonId: mloId,
        classId,
        contentId,
        isGradableItemsLockedByDefault,
      },
    },
    onError: handleErrorCallback,
    fetchPolicy: 'network-only',
  });
  const teacherResponseAttachment = get(
    state.selectedStudent,
    'submission.response.attachment',
  );
  const studentAttachment = get(state.selectedStudent, 'submission.attachment');
  const attachment = teacherResponseAttachment || studentAttachment || null;

  const classAssignmentData = data.classAssignment;

  useEffect(
    () => {
      if (classAssignmentData && classAssignmentData.groupedSubmissions) {
        dispatch({
          type: 'setupAssignmentData',
          payload: {
            groupedSubmissions: classAssignmentData.groupedSubmissions,
            selectedStudentId: studentId,
            assignment: classAssignmentData.assignment.assignmentInstance,
          },
        });
      }
    },
    [classAssignmentData, studentId],
  );

  useEffect(
    () => {
      dispatchAttachment({
        type: ATTACHMENT_CONTEXT_ACTIONS.updateAttachment,
        payload: attachment,
      });
    },
    [state.selectedStudent],
  );

  if (loading) {
    return <Spinner isCentered={true} testId="assignmentSpinner" />;
  }

  if (!classAssignmentData) {
    handleErrorCallback('No class assignment data received.');
    return <></>;
  }

  const openUnsavedChangesSubmissionConfirmationIf = (
    condition,
    action,
    message,
  ) => {
    if (condition) {
      dispatch({
        type: 'openUnsavedChangesSubmissionConfirmation',
        payload: {
          action,
          message,
        },
      });
    } else {
      action();
    }
  };

  const { language } = classAssignmentData.assignment.assignmentInstance;
  const goToDetailsPage = () =>
    openUnsavedChangesSubmissionConfirmationIf(
      state.isSaveButtonEnabled,
      () =>
        onGoBack({
          assignmentId,
          assignmentInstanceId,
          mloId,
          academicYearId,
          classId,
        }),
      t(
        'There are unsaved changes to the assignment. Do you want to discard the changes and navigate back to the Assignment Class List page?',
      ),
    );

  const canShowSubmissionDetails = () => {
    const {
      attachmentRequired,
      commentRequired,
    } = classAssignmentData.assignment.assignmentInstance;
    return attachmentRequired || commentRequired;
  };

  const assignmentTypeForPendo = assignmentType
    .split('_')
    .join('-')
    .toLowerCase();
  const showSubmissionDetails = canShowSubmissionDetails();

  return (
    <AssignmentContext.Provider
      value={{
        assignmentParams: {
          assignmentId,
          assignmentInstanceId,
          mloId,
          classId,
          finishExperienceContext,
        },
        assignmentData: {
          assignmentId:
            classAssignmentData.assignment.assignmentInstance.assignmentId,
          assignmentType,
        },
      }}
    >
      <ThemeProvider
        theme={{
          dir,
          lang: language,
          langCode: getLangCode(language),
        }}
      >
        <Layout
          onGoBack={goToDetailsPage}
          backButtonText={t('Back')}
          title={classAssignmentData.assignment.assignmentInstance.mloName}
          subjectCategory={
            classAssignmentData.assignment.assignmentInstance.subjectCategory
          }
        >
          {state.studentsCount > 0 && (
            <>
              {state.selectedStudentIndex !== 0 && (
                <PreviousStudentButton
                  className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-previous-student`}
                  onClick={() =>
                    openUnsavedChangesSubmissionConfirmationIf(
                      state.isSaveButtonEnabled,
                      () => {
                        dispatch({
                          type: 'restoreDefaults',
                        });
                        dispatchAttachment({
                          type: ATTACHMENT_CONTEXT_ACTIONS.removeAttachment,
                        });
                        onMoveToPrevStudent({
                          assignmentInstanceId,
                          studentId:
                            state.submittedStudents[
                              state.selectedStudentIndex - 1
                            ].student.id,
                          assignmentId,
                          mloId,
                          classId,
                          contentId,
                          academicYearId,
                        });
                      },
                      t(
                        'There are unsaved changes to the assignment. Do you want to discard the changes and navigate to the next student?',
                      ),
                    )
                  }
                  data-testid={'previous-student-arrow'}
                >
                  <ChevronLeft />
                </PreviousStudentButton>
              )}
              {state.selectedStudentIndex + 1 !== state.studentsCount && (
                <NextStudentButton
                  className={`data-analytics-${assignmentTypeForPendo}-student-submission-page-next-student`}
                  onClick={() =>
                    openUnsavedChangesSubmissionConfirmationIf(
                      state.isSaveButtonEnabled,
                      () => {
                        dispatch({
                          type: 'restoreDefaults',
                        });
                        dispatchAttachment({
                          type: ATTACHMENT_CONTEXT_ACTIONS.removeAttachment,
                        });
                        onMoveToNextStudent({
                          assignmentInstanceId,
                          studentId:
                            state.submittedStudents[
                              state.selectedStudentIndex + 1
                            ].student.id,
                          assignmentId,
                          mloId,
                          classId,
                          contentId,
                          academicYearId,
                        });
                      },
                      t(
                        'There are unsaved changes to the assignment. Do you want to discard the changes and navigate to the next student?',
                      ),
                    )
                  }
                  data-testid={'next-student-arrow'}
                >
                  <ChevronRight />
                </NextStudentButton>
              )}
              <UnsavedChangesSubmissionConfirmationModal />
            </>
          )}
          <Container>
            <Row>
              <Column
                colSpan={`${
                  !showSubmissionDetails ? 'col-12' : 'col-12 col-lg-4'
                }`}
              >
                <SubmissionInfo
                  onCancel={goToDetailsPage}
                  onSubmissionsReturn={returnedSubmissions => {
                    onSubmissionsReturn(
                      returnedSubmissions,
                      classAssignmentData.assignment.assignmentInstance
                        .maxScore,
                      // ALEF-93947: lets the host skip its own finish call only when we sent finishContext.
                      { finishedServerSide: Boolean(finishExperienceContext) },
                    );
                  }}
                />
              </Column>
              {showSubmissionDetails && (
                <Column colSpan="col-12 col-lg-8">
                  <SubmissionDetail />
                </Column>
              )}
            </Row>
          </Container>
        </Layout>
      </ThemeProvider>
    </AssignmentContext.Provider>
  );
};

export default StudentSubmission;

StudentSubmission.defaultProps = {
  teacherAssignmentParams: {},
  alefAssignmentParams: {},
  onGoBack: () => {},
  onMoveToNextStudent: () => {},
  onMoveToPrevStudent: () => {},
  onSubmissionsReturn: () => {},
  isGradableItemsLockedByDefault: false,
  finishExperienceContext: null,
};

StudentSubmission.propTypes = {
  teacherAssignmentParams: PropTypes.shape({
    assignmentInstanceId: PropTypes.string.isRequired,
    classId: PropTypes.string.isRequired,
    mloId: PropTypes.string.isRequired,
    contentId: PropTypes.string.isRequired,
  }),
  alefAssignmentParams: PropTypes.shape({
    assignmentId: PropTypes.string.isRequired,
    classId: PropTypes.string.isRequired,
    mloId: PropTypes.string.isRequired,
    contentId: PropTypes.string.isRequired,
  }),
  studentId: PropTypes.string.isRequired,
  assignmentType: PropTypes.string.isRequired,
  isGradableItemsLockedByDefault: PropTypes.bool.isRequired,
  onGoBack: PropTypes.func,
  onMoveToNextStudent: PropTypes.func,
  onMoveToPrevStudent: PropTypes.func,
  onSubmissionsReturn: PropTypes.func,
  finishExperienceContext: PropTypes.object,
};
